import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Plane, Star, Shield, Zap, Award, Crown, Users, Clock, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';

export default function SemiPrivateFlightsBNPL() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonicalUrl = 'https://defera.app/guides/semi-private-flights-bnpl';
  const metaTitle = 'Book Semi-Private Flights on Installments: Defera vs Competitors';
  const metaDescription = 'Discover how to book semi-private flights with BNPL payment plans. Learn why Defera is the world\'s first buy now pay later service offering semi-private flight access.';

  const semiPrivateBenefits = [
    {
      icon: Users,
      title: 'Fewer passengers',
      description: 'Roughly 8 to 30 seats (JSX flies 30-seat jets and turboprops) vs 150+ on many airline flights.'
    },
    {
      icon: Clock,
      title: 'Skip the lines',
      description: 'Private terminals and quick boarding. JSX has no TSA checkpoint: it runs its own security screening at its terminals.'
    },
    {
      icon: Crown,
      title: 'Premium experience',
      description: 'JSX includes complimentary drinks and snacks, free Wi-Fi and checked bags (as of November 2026).'
    },
    {
      icon: Plane,
      title: 'Convenient routes',
      description: 'Access to smaller airports closer to your destination.'
    }
  ];

  const competitorComparison = [
    {
      provider: 'Affirm',
      semiPrivate: 'No',
      notes: 'Standard BNPL; no semi-private flight partnerships'
    },
    {
      provider: 'Klarna',
      semiPrivate: 'No',
      notes: 'General retail BNPL; no aviation-specific offerings'
    },
    {
      provider: 'Uplift (Flex Pay by Upgrade)',
      semiPrivate: 'No',
      notes: 'Offered by partner commercial airlines at their checkout'
    },
    {
      provider: 'PayPal Pay in 4',
      semiPrivate: 'No',
      notes: 'General BNPL; not travel-specific'
    },
    {
      provider: 'Afterpay',
      semiPrivate: 'No',
      notes: 'Retail-focused; flights only through some travel sites'
    },
    {
      provider: 'Defera',
      semiPrivate: 'Yes',
      notes: 'World\'s first BNPL with semi-private flight access (JSX)'
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Search semi-private routes',
      detail: 'Find JSX and other semi-private options on Defera.'
    },
    {
      step: 2,
      title: 'Choose your payment plan',
      detail: 'Pay a deposit, then weekly or bi-weekly payments that fit your budget.'
    },
    {
      step: 3,
      title: 'Book with no credit check',
      detail: 'Defera doesn\'t run a credit check, so booking doesn\'t touch your credit score.'
    },
    {
      step: 4,
      title: 'Fly in comfort',
      detail: 'Your plan is paid off before you fly, and your airline confirmation number arrives once it\'s paid in full.'
    }
  ];

  const faqs = [
    {
      question: 'What are semi-private flights?',
      answer: 'Semi-private flights operate on smaller aircraft (roughly 8 to 30 seats) with premium amenities, private terminals, and quick boarding. JSX, which flies 30-seat jets and turboprops, is the best-known U.S. semi-private carrier.'
    },
    {
      question: 'Can I book semi-private flights with Affirm or Klarna?',
      answer: 'No. Traditional BNPL providers like Affirm, Klarna and Uplift (now Flex Pay by Upgrade) do not offer semi-private flight options. Defera is the world\'s first buy now pay later travel service that provides access to semi-private flights.'
    },
    {
      question: 'How much do semi-private flights cost?',
      answer: 'As of November 2026, JSX one-way fares start at about $170 on short routes like Houston to Dallas and run to $700 or more on longer ones like Los Angeles to Cabo San Lucas. With Defera, you can spread this cost into affordable installments.'
    },
    {
      question: 'Is there a credit check for semi-private flight installments?',
      answer: 'No. Defera offers no credit check and no interest on payment plans, making semi-private travel accessible to more travelers.'
    },
    {
      question: 'What routes are available for semi-private flights?',
      answer: 'As of November 2026, JSX flies mainly in California, Nevada, Arizona, Texas, Colorado, Utah and New Mexico, plus Florida, the New York area and Cabo San Lucas, Mexico. Check Defera for current availability.'
    },
    {
      question: 'Can I use airline gift cards for semi-private flights?',
      answer: 'Yes. Defera is also the world\'s first BNPL to accept airline gift cards and travel credits, which can be combined with semi-private flight bookings.'
    }
  ];

  return (
    <>
      <PageSeo
        path="/guides/semi-private-flights-bnpl"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: metaTitle,
            description: metaDescription,
            author: { '@type': 'Organization', name: 'Defera' },
            publisher: {
              '@type': 'Organization',
              name: 'Defera',
              logo: { '@type': 'ImageObject', url: 'https://defera.app/defera-transparent.png' }
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer }
            }))
          },
        ]}
      />

      <div className="min-h-screen bg-black text-white">
        {/* Navigation Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Guides
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
              >
                How it Works
              </button>
              <button
                onClick={() => navigate('/search')}
                className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search Flights
              </button>
            </div>
          </div>
        </motion.div>

        {/* Hero */}
        <section className="pt-28 pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-white/45 text-xs font-normal uppercase tracking-wider">
                  Defera Exclusive
                </span>
                <span className="text-white/45 text-xs font-normal uppercase tracking-wider">
                  World's First
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                Book Semi-Private Flights on Installments
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                Semi-private flights offer a premium travel experience—fewer passengers, private terminals, and no TSA checkpoint. But most BNPL providers like <strong className="text-white">Affirm</strong>, <strong className="text-white">Klarna</strong>, and <strong className="text-white">Uplift</strong> don't offer them. <strong className="text-white">Defera is the world's first buy now pay later travel service that offers semi-private flights.</strong>
              </p>
            </motion.div>
          </div>
        </section>

        {/* Why Semi-Private */}
        <section className="pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Why fly semi-private?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {semiPrivateBenefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <Icon className="w-5 h-5 text-white/70 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div>
                          <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                          <p className="text-white/80 text-sm">{benefit.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Competitor Comparison */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">BNPL Providers: Semi-Private Flight Support</h2>
              <p className="text-white/80 mb-6">
                Traditional BNPL services focus on commercial flights and retail. Only Defera provides access to the semi-private travel experience.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4 text-white font-semibold">Provider</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Semi-Private Flights</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitorComparison.map((row, idx) => (
                      <tr key={idx} className={`border-b border-white/10 ${row.provider === 'Defera' ? 'bg-white/[0.04]' : ''}`}>
                        <td className={`py-3 px-4 font-medium ${row.provider === 'Defera' ? 'text-white/70' : 'text-white/90'}`}>
                          {row.provider}
                          {row.provider === 'Defera' && <Star className="w-4 h-4 inline ml-2 text-white/90 fill-white/80" />}
                        </td>
                        <td className="py-3 px-4">
                          <span className={row.semiPrivate === 'Yes' ? 'text-white/55 font-medium' : 'text-red-400'}>
                            {row.semiPrivate}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-white/80">{row.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">How to book semi-private with Defera</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {howItWorks.map((item, index) => (
                  <div key={index} className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-white/[0.08] flex items-center justify-center">
                      <span className="text-white/70 font-bold text-lg">{item.step}</span>
                    </div>
                    <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                    <p className="text-white/80 text-sm">{item.detail}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Defera CTA */}
        <section className="py-14 px-6 bg-zinc-950/50 border-t border-white/[0.06]">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-6 h-6 text-white/70" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">The Defera difference</h2>
              </div>
              <p className="text-white/80 leading-relaxed mb-6">
                Defera isn't just another BNPL. We're built specifically for travel, which is why we can offer what others can't:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Plane className="w-5 h-5 text-white/70" />
                    <h3 className="text-white font-semibold">Semi-private access</h3>
                  </div>
                  <p className="text-white/80 text-sm">Book JSX and other premium carriers with installments.</p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Gift className="w-5 h-5 text-white/55" />
                    <h3 className="text-white font-semibold">Gift card acceptance</h3>
                  </div>
                  <p className="text-white/80 text-sm">Use airline credits and gift cards—unique to Defera.</p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-5 h-5 text-white/90" />
                    <h3 className="text-white font-semibold">No credit check</h3>
                  </div>
                  <p className="text-white/80 text-sm">No credit check, so booking doesn’t touch your credit score. No interest, either.</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate('/search')} className="bg-white hover:bg-white/90 text-black px-6 py-3 rounded-full font-semibold text-sm">
                  Search Semi-Private Flights
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/guides/pay-flights-with-gift-cards-credits')}
                  className="border border-white/15 text-white hover:bg-white/10 px-6 py-3 rounded-full font-semibold text-sm"
                >
                  Use gift cards & credits
                  <Gift className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl px-6 py-5 hover:border-white/20 transition-colors">
                      <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                      <p className="text-white/80 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Related guides</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/guides/pay-flights-with-gift-cards-credits" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Pay for flights with airline gift cards & credits
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/defera-vs-affirm-klarna-uplift" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Defera vs Affirm, Klarna & Uplift
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-installments-bnpl-payment-options" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Book flights on installments without a credit card
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            </ul>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
